import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import axios from "axios";
import {
  MapPin, Phone, Mail, Clock, Linkedin, ExternalLink, Send, CheckCircle, Globe
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  "Internet Service Provider",
  "IPTV Solutions",
  "Telecom Equipment",
  "System Integration",
  "IVR Solutions",
  "Website Hosting",
  "Web & App Development",
  "IP-1 Fiber Leasing",
  "MPLS Connectivity",
  "ISP Consulting",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Your message has been sent successfully!");
      setForm({ name: "", email: "", phone: "", service_interest: "", message: "" });
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Toaster position="top-right" richColors />

      {/* Hero */}
      <section data-testid="contact-hero" className="relative py-24 md:py-32 bg-[#0A192F] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-20 w-80 h-80 bg-[#EA580C] rounded-full blur-[100px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 block">
            Contact Us
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white font-['Outfit'] leading-[1.1] mb-6">
            Let's Build
            <br />
            <span className="text-[#F97316]">Something Together</span>
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">
            Whether you're looking for broadband connectivity, enterprise solutions, or partnership
            opportunities &mdash; we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Form + Info */}
      <section data-testid="contact-form-section" className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              <Card className="p-8 md:p-10 bg-white border border-[#E2E8F0] shadow-sm rounded-none">
                {submitted ? (
                  <div data-testid="form-success" className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-2xl font-semibold text-[#0A192F] font-['Outfit'] mb-2">
                      Message Sent!
                    </h3>
                    <p className="text-[#64748B] mb-6">
                      Thank you for reaching out. Our team will get back to you within 24 hours.
                    </p>
                    <Button
                      data-testid="send-another-btn"
                      onClick={() => setSubmitted(false)}
                      variant="outline"
                      className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white rounded-sm"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <h3 className="text-xl font-semibold text-[#0A192F] font-['Outfit'] mb-6">
                      Send Us a Message
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                      <div>
                        <Label htmlFor="name" className="text-sm text-[#334155] mb-2 block">
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          data-testid="contact-name-input"
                          placeholder="Your full name"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          className="rounded-sm border-[#E2E8F0] focus:border-[#EA580C] focus:ring-[#EA580C]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-sm text-[#334155] mb-2 block">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="contact-email-input"
                          placeholder="you@company.com"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="rounded-sm border-[#E2E8F0] focus:border-[#EA580C] focus:ring-[#EA580C]"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                      <div>
                        <Label htmlFor="phone" className="text-sm text-[#334155] mb-2 block">
                          Phone Number
                        </Label>
                        <Input
                          id="phone"
                          data-testid="contact-phone-input"
                          placeholder="+91 XXXXX XXXXX"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          className="rounded-sm border-[#E2E8F0] focus:border-[#EA580C] focus:ring-[#EA580C]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="service" className="text-sm text-[#334155] mb-2 block">
                          Service Interest
                        </Label>
                        <Select
                          value={form.service_interest}
                          onValueChange={(val) => setForm({ ...form, service_interest: val })}
                        >
                          <SelectTrigger
                            data-testid="contact-service-select"
                            className="rounded-sm border-[#E2E8F0]"
                          >
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {serviceOptions.map((svc) => (
                              <SelectItem key={svc} value={svc}>
                                {svc}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="mb-6">
                      <Label htmlFor="message" className="text-sm text-[#334155] mb-2 block">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        data-testid="contact-message-input"
                        placeholder="Tell us about your requirements..."
                        rows={5}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="rounded-sm border-[#E2E8F0] focus:border-[#EA580C] focus:ring-[#EA580C] resize-none"
                      />
                    </div>
                    <Button
                      type="submit"
                      data-testid="contact-submit-btn"
                      disabled={loading}
                      className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm px-8 py-5 text-sm font-semibold w-full md:w-auto"
                    >
                      {loading ? "Sending..." : "Send Message"} <Send className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                )}
              </Card>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="p-6 bg-white border border-[#E2E8F0] shadow-sm rounded-none">
                <h4 className="text-sm font-bold uppercase tracking-[0.15em] text-[#EA580C] mb-5">
                  Office Address
                </h4>
                <div className="flex gap-3 items-start mb-4">
                  <MapPin className="w-5 h-5 text-[#0A192F] mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-[#334155] leading-relaxed">
                    #330, Whitecity Layout,<br />
                    Opp to Blossom International School,<br />
                    Bengaluru - 560049, India
                  </p>
                </div>
                <div className="flex gap-3 items-center mb-4">
                  <Phone className="w-5 h-5 text-[#0A192F] flex-shrink-0" />
                  <a href="tel:+919986456688" className="text-sm text-[#334155] hover:text-[#EA580C] transition-colors">
                    +91 99864 56688
                  </a>
                </div>
                <div className="flex gap-3 items-center mb-4">
                  <Mail className="w-5 h-5 text-[#0A192F] flex-shrink-0" />
                  <a href="mailto:info@assistivenetwork.com" className="text-sm text-[#334155] hover:text-[#EA580C] transition-colors">
                    info@assistivenetwork.com
                  </a>
                </div>
                <div className="flex gap-3 items-center">
                  <Clock className="w-5 h-5 text-[#0A192F] flex-shrink-0" />
                  <p className="text-sm text-[#334155]">Mon-Sat: 9:00 AM - 6:00 PM IST</p>
                </div>
              </Card>

              <Card className="p-6 bg-white border border-[#E2E8F0] shadow-sm rounded-none">
                <h4 className="text-sm font-bold uppercase tracking-[0.15em] text-[#EA580C] mb-5">
                  Connect With Us
                </h4>
                <div className="space-y-3">
                  <a
                    href="https://www.linkedin.com/company/13628123/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-linkedin"
                    className="flex items-center gap-3 p-3 border border-[#E2E8F0] hover:border-[#EA580C] transition-colors group"
                  >
                    <Linkedin className="w-5 h-5 text-[#0A192F] group-hover:text-[#EA580C]" />
                    <span className="text-sm text-[#334155] group-hover:text-[#EA580C]">LinkedIn</span>
                    <ExternalLink className="w-3 h-3 text-[#64748B] ml-auto" />
                  </a>
                  <a
                    href="https://www.justdial.com/Bangalore/Assistive-Netspeed-Technologies-Pvt-Ltd-Opposite-To-Blossom-International-School-Seegehalli/080PXX80-XX80-240527220044-Z6W9_BZDET"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-justdial"
                    className="flex items-center gap-3 p-3 border border-[#E2E8F0] hover:border-[#EA580C] transition-colors group"
                  >
                    <Globe className="w-5 h-5 text-[#0A192F] group-hover:text-[#EA580C]" />
                    <span className="text-sm text-[#334155] group-hover:text-[#EA580C]">JustDial</span>
                    <ExternalLink className="w-3 h-3 text-[#64748B] ml-auto" />
                  </a>
                </div>
              </Card>

              <Card className="p-6 bg-[#0A192F] border-none rounded-none">
                <h4 className="text-sm font-bold uppercase tracking-[0.15em] text-[#EA580C] mb-3">
                  Business Hours
                </h4>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Our support team is available Monday through Saturday, 9 AM to 6 PM IST.
                  For network emergencies, our NOC operates 24/7.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section data-testid="map-section" className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 block">
            Find Us
          </span>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#0A192F] font-['Outfit'] mb-8">
            Our Location
          </h2>
        </div>
        <iframe
          data-testid="google-map"
          title="Assistive Netspeed Technologies Office Location"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.4!2d77.7!3d13.0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDAwJzAwLjAiTiA3N8KwNDInMDAuMCJF!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
          width="100%"
          height="450"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="w-full"
        />
      </section>
    </div>
  );
}
